import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BrandMark, Wordmark } from "./BrandMark";

export default function Header() {
  const { t, lang, changeLang, langs } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "work", label: t.nav.work },
    { id: "projects", label: t.nav.projects },
    { to: "/blog", label: t.nav.blog },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/case-studies", label: t.nav.cases },
    { id: "contact", label: t.nav.contact },
  ];

  const goSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollTo = (id) => goSection(id);

  const handleNav = (link) => {
    setMobileOpen(false);
    if (link.to) navigate(link.to);
    else goSection(link.id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "glass-strong border-b border-white/10" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[74px] flex items-center justify-between gap-4">
        {/* Logo */}
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-3 shrink-0" data-testid="header-logo">
          <BrandMark size={46} />
          <Wordmark lang={lang} />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id || l.to}
              onClick={() => handleNav(l)}
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative group"
              data-testid={`nav-${l.id || l.to.replace("/", "")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 transition-transform origin-center" style={{ background: "var(--primary)" }} />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-sm text-slate-200 hover:border-white/30 transition-colors"
              data-testid="lang-switcher"
            >
              <Globe size={16} />
              <span className="hidden sm:inline">{langs.find((l) => l.code === lang)?.label}</span>
              <ChevronDown size={14} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute end-0 mt-2 w-40 glass-strong border border-white/10 rounded-xl overflow-hidden shadow-2xl"
                >
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { changeLang(l.code); setLangOpen(false); }}
                      className={`w-full text-start px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === l.code ? "text-primary font-bold" : "text-slate-200"}`}
                      data-testid={`lang-option-${l.code}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => goSection("contact")}
            className="hidden sm:inline-flex items-center rounded-full px-5 py-2.5 text-sm font-bold text-[#04120a] transition-transform hover:scale-105"
            style={{ background: "var(--primary)" }}
            data-testid="header-cta"
          >
            {t.cta.quote}
          </button>

          <button className="lg:hidden text-white p-2" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass-strong border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button key={l.id || l.to} onClick={() => handleNav(l)} className="text-start py-3 text-slate-200 border-b border-white/5" data-testid={`mobile-nav-${l.id || l.to.replace("/", "")}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => { setMobileOpen(false); goSection("contact"); }} className="mt-3 rounded-full px-5 py-3 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>
                {t.cta.quote}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
